import React from 'react';
import PropTypes from 'prop-types';
import NextImage from 'next/image';
import capitalize from 'lodash/capitalize';
import { VisuallyHidden, Tooltip, PillText } from '@web/atoms';
import checkIcon from '../../images/Icons/check-in-circle-lilac.svg';
import {
  Card,
  CardWrapper,
  Info,
  StyledImage,
  Name,
  Description,
  CardContent,
  CardHeader,
  MoreDetails,
  CheckboxContainer,
  Label,
  LabelText,
  ButtonWrapper,
  BadgeWrapper,
  IconContainer,
} from './styles.js';

export const RecommendedProductCard = ({
  label,
  title,
  badge,
  badgeVariant = 'dark',
  description,
  checked = false,
  img,
  imgSize,
  onChange,
  onDetailsClick,
  showMoreDetails,
  unavailable,
  unavailableText = 'Unavailable due to allergy concern',
  notSelectable,
  notSelectableText = 'To choose this item, please remove one of your previous selections.',
  level,
  orientation = 'row',
  offsetBadge = false,
  showSelectionButton = false,
  buttonIcon,
  width = 345,
  bgColor = 'lightest',
  showDescription = true,
  typography = 'heading4',
  showCheckmark = false,
  alignColumnContent = 'left',
}) => {
  let labelText = label;
  if (unavailable) {
    labelText = unavailableText || 'Recipe Unavailable';
  } else if (checked) {
    labelText = 'In your order';
  }

  const imgWidth = imgSize ?? img.width ?? 72;
  const imgHeight = imgSize ?? img.height ?? 72;

  const getProductCard = props => (
    <CardWrapper $offsetBadge={offsetBadge ? 10 : 0}>
      {offsetBadge && badge && (
        <BadgeWrapper>
          <PillText text={badge} variant={badgeVariant} size="large" />
        </BadgeWrapper>
      )}
      <Card
        orientation={orientation}
        {...props}
        checked={checked}
        width={width}
        unavailable={unavailable}
        $notSelectable={notSelectable}
        $bgColor={bgColor}
        aria-label={`${label} ${title}`}
        onClick={!notSelectable && onChange}
        showSelectionButton={showSelectionButton}
        showDescription={showDescription}
        $offsetBadge={offsetBadge}
      >
        <CardContent
          orientation={orientation}
          showSelectionButton={showSelectionButton}
          $offsetBadge={offsetBadge && badge}
          showDescription={showDescription}
          $alignColumnContent={alignColumnContent}
        >
          {orientation === 'column' &&
            badge &&
            !offsetBadge &&
            !showDescription && (
              <PillText text={badge} variant={badgeVariant} size="small" />
            )}
          <StyledImage
            checked={checked}
            unavailable={unavailable}
            orientation={orientation}
            $imgWidth={imgSize}
            $imgHeight={imgSize}
            showDescription={showDescription}
            $alignColumnContent={alignColumnContent}
          >
            {img && (
              <NextImage
                src={img.src}
                alt={img.alt}
                width={imgWidth}
                height={imgHeight}
              />
            )}
          </StyledImage>
          {showCheckmark && (
            <IconContainer
              $orientation={orientation}
              $offsetBadge={offsetBadge}
            >
              {checked && (
                <NextImage
                  src={checkIcon}
                  width={28}
                  height={28}
                  alt={'Check icon'}
                />
              )}
            </IconContainer>
          )}
          <Info checked={checked} $orientation={orientation}>
            <CardHeader
              $orientation={orientation}
              showDescription={showDescription}
              $alignColumnContent={alignColumnContent}
            >
              <Name as={level} $typography={typography}>
                {title}
              </Name>
            </CardHeader>
            {showDescription && (
              <Description>
                {unavailable ? unavailableText : description}
              </Description>
            )}
            {showMoreDetails && !unavailable && (
              <MoreDetails
                type="button"
                unavailable={unavailable}
                onClick={e => {
                  e.stopPropagation();
                  onDetailsClick();
                }}
                disabled={!showMoreDetails}
              >
                View Ingredients
              </MoreDetails>
            )}
          </Info>
          {showSelectionButton && (
            <ButtonWrapper>
              <CheckboxContainer
                checked={checked && !unavailable}
                unavailable={unavailable}
                icon={buttonIcon}
              >
                <Label
                  htmlFor={`${label}-checkbox-input`}
                  checked={checked && !unavailable}
                  unavailable={unavailable}
                  icon={buttonIcon}
                >
                  <VisuallyHidden>
                    {label + ' ' + capitalize(label)}
                  </VisuallyHidden>
                </Label>
                <LabelText
                  checked={checked}
                  unavailable={unavailable || notSelectable}
                >
                  {labelText}
                </LabelText>
              </CheckboxContainer>
            </ButtonWrapper>
          )}
        </CardContent>
      </Card>
    </CardWrapper>
  );

  return notSelectable && !unavailable ? (
    <Tooltip trigger={getProductCard} placement="top" offsetY={-200}>
      {notSelectableText}
    </Tooltip>
  ) : (
    getProductCard()
  );
};

RecommendedProductCard.displayName = 'RecommendedProductCard';
RecommendedProductCard.propTypes = {
  label: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  benefit: PropTypes.string,
  unavailable: PropTypes.bool,
  unavailableText: PropTypes.string,
  unavailableLabel: PropTypes.string,
  notSelectable: PropTypes.bool,
  notSelectableText: PropTypes.string,
  value: PropTypes.string.isRequired,
  description: PropTypes.node.isRequired,
  checked: PropTypes.bool,
  img: PropTypes.shape({
    src: PropTypes.string.isRequired,
    alt: PropTypes.string.isRequired,
  }).isRequired,
  imgSize: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  callout: PropTypes.string,
  showPriceText: PropTypes.bool,
  period: PropTypes.string,
  badge: PropTypes.string,
  badgeVariant: PropTypes.string,
  level: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
  offsetBadge: PropTypes.bool,
  showSelectionButton: PropTypes.bool,
  buttonIcon: PropTypes.string,
  width: PropTypes.number,
  bgColor: PropTypes.oneOf(['base', 'lightest']),
  showDescription: PropTypes.bool,
  typography: PropTypes.string,
  showCheckmark: PropTypes.bool,
};
